/**
 * Voci di navigazione principale.
 * ---------------------------------------------------------------------------
 * Le etichette usano le chiavi i18n GIÀ TRADOTTE in 11 lingue
 * (`nav_*` nel dizionario migrato). Non si inventano etichette nuove dove
 * esiste già la chiave.
 *
 * `to` usa la nuova struttura URL (decisione 18): senza `/puglia/`, senza
 * `.html`, con trailing slash.
 *
 * STRUTTURA (allineamento visual-diff alla home del riferimento, 29/07/2026):
 * il menu di primo livello ha DUE sole voci, entrambe con dropdown
 * («Comuni», «Rete Civica» — costruite in Header.tsx sui dati di
 * `features/mappa/comuni.ts`); tutte le altre destinazioni stanno nel
 * megamenu del pulsante ☰ (`MAIN_NAV` qui sotto).
 */

export interface NavItem {
  readonly to: string;
  /** Chiave del dizionario i18n. */
  readonly labelKey: string;
  /** Testo italiano di riserva, se la chiave non esistesse. */
  readonly fallback: string;
}

/** Destinazioni del megamenu ☰ («Tutte le sezioni»). */
export const MAIN_NAV: readonly NavItem[] = [
  { to: '/province/', labelKey: 'nav_province', fallback: 'Province' },
  { to: '/eventi/', labelKey: 'nav_eventi', fallback: 'Eventi' },
  { to: '/servizi/', labelKey: 'nav_servizi', fallback: 'Servizi' },
  { to: '/territorio/esplora/', labelKey: 'nav_esplora', fallback: 'Esplora' },
  { to: '/opportunita/', labelKey: 'nav_opportunita', fallback: 'Opportunità' },
  { to: '/news/', labelKey: 'nav_news', fallback: 'News' },
  { to: '/progetto/', labelKey: 'nav_progetto', fallback: 'Il Progetto' },
];

/**
 * Le 4 sezioni del dropdown «Rete Civica», come sul riferimento
 * (Lavoro & Servizi · Start-up & Imprese · Enti del Terzo Settore ·
 * Volontariato). Le destinazioni sono le rotte LOCALI più vicine per
 * contenuto: le pagine dedicate del riferimento (lavoro/, start-up/,
 * enti-terzo-settore/, volontariato/) non esistono ancora in questo portale.
 */
/** Accento della piastrella icona (token `--ic-rete-*` / `--ic-navy-800`). */
export type ReteAccent = 'lavoro' | 'startup' | 'ets' | 'volontariato';

export interface ReteSection extends NavItem {
  readonly descKey: string;
  readonly descFallback: string;
  /** Badge di categoria accanto al titolo (Bacheca / Vetrina / …). */
  readonly tagKey: string;
  readonly tagFallback: string;
  readonly accent: ReteAccent;
  /** Path svg 24×24 della piastrella icona, dal riferimento (CSP: inline). */
  readonly iconPath: string;
}

export const RETE_CIVICA_SECTIONS: readonly ReteSection[] = [
  {
    to: '/rete-civica/jobcoin/',
    labelKey: 'rete_lavoro_title',
    fallback: 'Lavoro & Servizi',
    descKey: 'rete_lavoro_desc',
    descFallback: 'Offri o cerca prestazioni e servizi tra cittadini e imprese.',
    tagKey: 'rete_lavoro_tag',
    tagFallback: 'Bacheca',
    accent: 'lavoro',
    iconPath:
      'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2 2.3-2.3Z',
  },
  {
    to: '/attivita/',
    labelKey: 'rete_startup_title',
    fallback: 'Start-up & Imprese',
    descKey: 'rete_startup_desc',
    descFallback: 'Professionisti, PMI e start-up del territorio, con badge verificato.',
    tagKey: 'rete_startup_tag',
    tagFallback: 'Vetrina',
    accent: 'startup',
    iconPath: 'M3 9 12 3l9 6M5 10v10h14V10M9 20v-6h6v6',
  },
  {
    to: '/rete-civica/partecipa/',
    labelKey: 'rete_ets_title',
    fallback: 'Enti del Terzo Settore',
    descKey: 'rete_ets_desc',
    descFallback: 'Associazioni ed ETS: chi sono, cosa fanno, come sostenerli.',
    tagKey: 'rete_ets_tag',
    tagFallback: 'Comunità',
    accent: 'ets',
    iconPath: 'M12 21s-7-4.5-9-9a5 5 0 0 1 9-2 5 5 0 0 1 9 2c-2 4.5-9 9-9 9Z',
  },
  {
    to: '/rete-civica/cittadinanza-attiva/',
    labelKey: 'rete_volontariato_title',
    fallback: 'Volontariato',
    descKey: 'rete_volontariato_desc',
    descFallback: 'Opportunità e cause del territorio: candidati e partecipa.',
    tagKey: 'rete_volontariato_tag',
    tagFallback: 'Sociale',
    accent: 'volontariato',
    iconPath:
      'M20 7h-7l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2ZM12 12v5M9.5 14.5h5',
  },
];

/** Link del footer, raggruppati. Chiavi `ftr_*`/`top_footer_*` già tradotte. */
export interface FooterGroup {
  readonly titleKey: string;
  readonly titleFallback: string;
  readonly items: readonly NavItem[];
}

export const FOOTER_GROUPS: readonly FooterGroup[] = [
  {
    titleKey: 'ftr_explore_title',
    titleFallback: 'Esplora',
    items: [
      // Le quattro voci del riferimento (visual-diff 29/07/2026).
      { to: '/province/', labelKey: 'nav_province', fallback: 'Province' },
      { to: '/servizi/', labelKey: 'nav_servizi', fallback: 'Servizi' },
      { to: '/eventi/', labelKey: 'nav_eventi', fallback: 'Eventi' },
      { to: '/news/', labelKey: 'nav_news', fallback: 'News' },
    ],
  },
  {
    titleKey: 'top_footer_istituzionale',
    titleFallback: 'Istituzionale',
    items: [
      { to: '/progetto/', labelKey: 'top_footer_mission', fallback: 'Mission' },
      { to: '/rete-civica/', labelKey: 'nav_rete', fallback: 'Rete Civica' },
      {
        to: '/rete-civica/attiva-comune/',
        labelKey: 'top_footer_per_comuni',
        fallback: 'Per i Comuni',
      },
      {
        to: '/progetto/dati-aperti/',
        labelKey: 'top_footer_trasparenza',
        fallback: 'Dati aperti',
      },
    ],
  },
  {
    // Chiave NUOVA (docs/i18n-migrazione.md): `ftr_links` nel dizionario
    // migrato vale «Esplora» e avrebbe dato alla nav legale lo stesso nome
    // della colonna Esplora — due landmark indistinguibili.
    titleKey: 'ftr_legal_label',
    titleFallback: 'Note legali',
    items: [
      { to: '/legale/privacy/', labelKey: 'ftr_privacy', fallback: 'Privacy Policy' },
      { to: '/legale/cookie/', labelKey: 'ftr_cookie', fallback: 'Cookie Policy' },
      {
        to: '/legale/accessibilita/',
        labelKey: 'ftr_a11y',
        fallback: 'Dichiarazione di accessibilità',
      },
    ],
  },
];
